/**
 * Static reference data for the MCP server documentation page. The API exposes a single
 * Streamable-HTTP endpoint at `POST {VITE_API_URL}/api/mcp`, authenticated with OAuth 2.1 — the
 * client registers itself and the user approves it on the consent screen, so no snippet here ever
 * carries a credential (see apps/api/src/mcp-oauth + docs/mcp-client-setup.md).
 */

/** Per-grant fixed-window rate limit (apps/api/src/mcp/constants.ts defaults). */
export const MCP_RATE_LIMIT_REQUESTS = 120;
export const MCP_RATE_LIMIT_WINDOW_SECONDS = 60;

/** Builds the fully-qualified MCP endpoint from the configured API base URL. */
export const buildMcpEndpoint = (apiUrl: string): string => `${apiUrl}/api/mcp`;

export interface McpClientSnippet {
	id: string;
	titleKey: string;
	descriptionKey: string;
	/** Highlight hint / label for the code block (e.g. "json", "bash"). */
	language: string;
	build: (endpoint: string) => string;
}

/**
 * Copy-paste configuration for the MCP clients we document. Each one only needs the endpoint:
 * authentication happens in the browser through the OAuth consent screen, so there is no key to
 * paste and nothing secret in these snippets.
 */
export const MCP_CLIENT_SNIPPETS: McpClientSnippet[] = [
	{
		id: "claudeCode",
		titleKey: "integrations.mcp.clients.claudeCode.title",
		descriptionKey: "integrations.mcp.clients.claudeCode.description",
		language: "bash",
		build: (endpoint) => `claude mcp add --transport http signalist ${endpoint}`,
	},
	{
		id: "cursor",
		titleKey: "integrations.mcp.clients.cursor.title",
		descriptionKey: "integrations.mcp.clients.cursor.description",
		language: "json",
		build: (endpoint) => JSON.stringify({ mcpServers: { signalist: { url: endpoint } } }, null, 2),
	},
	{
		id: "claudeDesktop",
		titleKey: "integrations.mcp.clients.claudeDesktop.title",
		descriptionKey: "integrations.mcp.clients.claudeDesktop.description",
		language: "json",
		build: (endpoint) =>
			JSON.stringify(
				{ mcpServers: { signalist: { command: "npx", args: ["mcp-remote", endpoint] } } },
				null,
				2
			),
	},
	{
		id: "generic",
		titleKey: "integrations.mcp.clients.generic.title",
		descriptionKey: "integrations.mcp.clients.generic.description",
		language: "bash",
		build: (endpoint) =>
			[
				`Transport: Streamable HTTP (POST)`,
				`URL:       ${endpoint}`,
				`Auth:      OAuth 2.1 (authorization code + PKCE, scope "mcp")`,
			].join("\n"),
	},
];

/** i18n keys for the "what you can do" capability bullets. */
export const MCP_CAPABILITY_KEYS = [
	"integrations.mcp.capabilities.items.search",
	"integrations.mcp.capabilities.items.research",
	"integrations.mcp.capabilities.items.knowledgeDb",
	"integrations.mcp.capabilities.items.jobs",
];
